/**
 * Merkle Directory Tree — CAS-backed directory objects (§11 v3)
 *
 * Each directory is an independent encrypted JSON object, referenced by SHA-256 hash.
 * Tree structure enables O(1) unchanged-subtree skipping during sync and merge.
 *
 * Directory object format:
 * {
 *   type: "secureblob-dir",
 *   entries: [
 *     { name, kind, id?, mime?, size, mtime, ctime, content?, dirHash?, target?, meta? }
 *   ]
 * }
 *
 * Entries are sorted by `name` using ordinal comparison for cross-platform determinism.
 */

import type { ContentRef, CryptoProvider } from '../types'
import { canonicalize, buildAad } from './state'

// ── Directory Entry types ────────────────────────────────────────

export interface DirEntryFile {
    name: string
    kind: 'file'
    id?: string              // UUID — stable across renames (future CRDT tracking)
    mime: string
    size: number
    mtime: number            // epoch ms
    ctime: number            // epoch ms — creation time, immutable
    content: ContentRef
    lastWriterKid?: string
    meta?: Record<string, unknown>
}

export interface DirEntryDir {
    name: string
    kind: 'directory'
    id?: string
    mtime: number
    ctime: number
    dirHash: string          // SHA-256 hash of the child directory object
    meta?: Record<string, unknown>
}

export interface DirEntrySymlink {
    name: string
    kind: 'symlink'
    id?: string
    target: string           // relative or absolute path
    mtime: number
    ctime: number
    lastWriterKid?: string
    meta?: Record<string, unknown>
}

export type DirEntry = DirEntryFile | DirEntryDir | DirEntrySymlink

// ── Directory Object ─────────────────────────────────────────────

export interface DirectoryObject {
    type: 'secureblob-dir'
    entries: DirEntry[]
}

// ── Codec deps ───────────────────────────────────────────────────

export interface DirTreeCodecDeps {
    crypto: CryptoProvider
    stateKey: Uint8Array
    blobId: string
}

// ── Serialization ────────────────────────────────────────────────

/**
 * Sort entries by name using ordinal comparison.
 * This MUST match the canonicalize() key sort for cross-platform determinism.
 */
export function sortEntries(entries: DirEntry[]): DirEntry[] {
    return [...entries].sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
}

/**
 * Create a canonical directory object from entries.
 * Entries are sorted by name before serialization.
 */
export function createDirObject(entries: DirEntry[]): DirectoryObject {
    return {
        type: 'secureblob-dir',
        entries: sortEntries(entries),
    }
}

/**
 * Serialize a directory object to deterministic canonical JSON bytes.
 */
export function serializeDirObject(dir: DirectoryObject): Uint8Array {
    return new TextEncoder().encode(canonicalize(dir))
}

/**
 * Deserialize directory object from decrypted bytes.
 */
export function deserializeDirObject(bytes: Uint8Array): DirectoryObject {
    return JSON.parse(new TextDecoder().decode(bytes)) as DirectoryObject
}

// ── Encrypt / Decrypt ────────────────────────────────────────────

/**
 * Encrypt a directory object using content-addressed deterministic encryption.
 * Returns { ciphertext, hash } where hash is the CAS key.
 *
 * Same directory content → same ciphertext → same hash (content-addressed dedup).
 */
export async function encryptDirObject(
    dir: DirectoryObject,
    deps: DirTreeCodecDeps,
): Promise<{ ciphertext: Uint8Array; hash: string }> {
    const canonical = serializeDirObject(dir)
    const aad = buildAad(deps.blobId, 'dir')
    const ciphertext = await deps.crypto.encryptDeterministic(deps.stateKey, canonical, aad)
    const hash = await deps.crypto.sha256(ciphertext)
    return { ciphertext, hash }
}

/**
 * Decrypt a directory object from CAS bytes.
 */
export async function decryptDirObject(
    ciphertext: Uint8Array,
    deps: DirTreeCodecDeps,
): Promise<DirectoryObject> {
    const aad = buildAad(deps.blobId, 'dir')
    const plaintext = await deps.crypto.decrypt(deps.stateKey, ciphertext, aad)
    return deserializeDirObject(plaintext)
}

// ── Tree operations ──────────────────────────────────────────────

export interface DirObjectStore {
    get(hash: string): Promise<Uint8Array | null>
    put(hash: string, bytes: Uint8Array): Promise<void>
    has(hash: string): Promise<boolean>
}

/** Maximum tree depth before aborting (cyclic graph protection). */
const MAX_TREE_DEPTH = 256

/**
 * Build a full directory tree from a flat list of file/directory paths.
 * Used for initial workspace creation / migration from v1 state.
 *
 * Returns a map of dirHash → ciphertext for ALL directory objects,
 * plus the rootDirHash.
 *
 * Accepts both file entries and explicit directory entries.
 * Directory entries ensure empty dirs survive the round-trip.
 */
export async function buildTreeFromPaths(
    files: Array<{
        path: string
        entry: Omit<DirEntryFile, 'name'> | Omit<DirEntryDir, 'name'>
    }>,
    deps: DirTreeCodecDeps,
): Promise<{ rootDirHash: string; objects: Map<string, Uint8Array> }> {
    // Group files by directory
    const dirMap = new Map<string, DirEntry[]>()

    // Track explicit empty directory entries with their metadata
    const explicitDirMeta = new Map<string, { mtime: number; ctime: number; dirHash?: string; meta?: Record<string, unknown> }>()

    for (const file of files) {
        // Handle explicit directory entries (from mkdir)
        if (file.entry.kind === 'directory') {
            // Strip trailing slash for canonical dir path
            const dirPath = file.path.replace(/\/+$/, '') || '/'
            if (!dirMap.has(dirPath)) dirMap.set(dirPath, [])
            explicitDirMeta.set(dirPath, {
                mtime: (file.entry as Omit<DirEntryDir, 'name'>).mtime,
                ctime: (file.entry as Omit<DirEntryDir, 'name'>).ctime,
                dirHash: (file.entry as Omit<DirEntryDir, 'name'>).dirHash || undefined,
                meta: (file.entry as Omit<DirEntryDir, 'name'>).meta,
            })
            // Ensure all ancestor directories exist
            const parts = dirPath.split('/').filter(Boolean)
            for (let i = 0; i < parts.length; i++) {
                const ancestorPath = i === 0 ? '/' : '/' + parts.slice(0, i).join('/')
                if (!dirMap.has(ancestorPath)) dirMap.set(ancestorPath, [])
            }
            continue
        }

        const parts = file.path.split('/').filter(Boolean)
        const fileName = parts.pop()!
        const dirPath = '/' + parts.join('/')

        if (!dirMap.has(dirPath)) dirMap.set(dirPath, [])
        dirMap.get(dirPath)!.push({
            ...file.entry,
            name: fileName,
        } as DirEntryFile)

        // Ensure all ancestor directories exist in the map
        for (let i = 0; i < parts.length; i++) {
            const ancestorPath = '/' + parts.slice(0, i).join('/')
            if (!dirMap.has(ancestorPath)) dirMap.set(ancestorPath, [])
        }
    }

    // Add empty root if missing
    if (!dirMap.has('/')) dirMap.set('/', [])

    const objects = new Map<string, Uint8Array>()

    // Build tree bottom-up: sort directories by depth (deepest first)
    const dirPaths = [...dirMap.keys()].sort((a, b) => {
        const depthA = a === '/' ? 0 : a.split('/').length - 1
        const depthB = b === '/' ? 0 : b.split('/').length - 1
        return depthB - depthA  // deepest first
    })

    const dirHashes = new Map<string, string>()

    for (const dirPath of dirPaths) {
        const entries = dirMap.get(dirPath)!
        const explicit = explicitDirMeta.get(dirPath)
        const hasKnownDescendants = [...dirMap.keys()].some(childPath =>
            childPath !== dirPath && childPath.startsWith(dirPath === '/' ? '/' : `${dirPath}/`),
        )
        if (explicit?.dirHash && entries.length === 0 && !hasKnownDescendants) {
            dirHashes.set(dirPath, explicit.dirHash)
            continue
        }

        // Add sub-directory entries for any direct children dirs
        for (const [childPath,] of dirMap) {
            if (childPath === dirPath) continue
            // Check if childPath is a direct child of dirPath
            const parent = childPath === '/' ? '' : childPath.substring(0, childPath.lastIndexOf('/')) || '/'
            if (parent === dirPath) {
                const childName = childPath.split('/').pop()!
                const childHash = dirHashes.get(childPath)
                if (childHash && !entries.some(e => e.name === childName)) {
                    // Use explicit dir metadata if available, otherwise derive from children
                    const explicit = explicitDirMeta.get(childPath)
                    const childEntries = dirMap.get(childPath) ?? []
                    const maxMtime = explicit?.mtime ?? childEntries.reduce(
                        (mx, e) => Math.max(mx, 'mtime' in e ? (e as any).mtime ?? 0 : 0), 0,
                    )
                    const ctime = explicit?.ctime ?? maxMtime
                    entries.push({
                        name: childName,
                        kind: 'directory',
                        mtime: maxMtime,
                        ctime,
                        dirHash: childHash,
                        ...(explicit?.meta ? { meta: explicit.meta } : {}),
                    } as DirEntryDir)
                }
            }
        }

        const dir = createDirObject(entries)
        const { ciphertext, hash } = await encryptDirObject(dir, deps)
        objects.set(hash, ciphertext)
        dirHashes.set(dirPath, hash)
    }

    const rootDirHash = dirHashes.get('/')!
    return { rootDirHash, objects }
}

/**
 * Walk the tree to resolve a path to its DirEntry.
 *
 * @param path - absolute path like "/docs/readme.md"
 * @param rootDirHash - hash of the root directory object
 * @param store - object store for fetching directory objects
 * @param deps - codec dependencies
 * @returns the entry and its parent directory, or null if not found
 */
export async function resolvePath(
    path: string,
    rootDirHash: string,
    store: DirObjectStore,
    deps: DirTreeCodecDeps,
): Promise<{ entry: DirEntry; parentDir: DirectoryObject; parentHash: string } | null> {
    const parts = path.split('/').filter(Boolean)
    if (parts.length === 0) return null  // root itself is a directory, not an entry

    let currentHash = rootDirHash
    const visited = new Set<string>()  // cyclic graph protection

    for (let i = 0; i < parts.length; i++) {
        if (visited.has(currentHash)) {
            throw new Error(`Cyclic directory reference detected at hash ${currentHash}`)
        }
        visited.add(currentHash)

        if (visited.size > MAX_TREE_DEPTH) {
            throw new Error(`Directory tree exceeds maximum depth (${MAX_TREE_DEPTH})`)
        }

        const ciphertext = await store.get(currentHash)
        if (!ciphertext) return null

        const dir = await decryptDirObject(ciphertext, deps)
        const name = parts[i]
        const entry = dir.entries.find(e => e.name === name)
        if (!entry) return null

        // Last segment → found the target
        if (i === parts.length - 1) {
            return { entry, parentDir: dir, parentHash: currentHash }
        }

        // Must be a directory to continue traversal
        if (entry.kind !== 'directory') return null
        currentHash = entry.dirHash
    }

    return null
}

/**
 * List entries in a directory at the given path.
 *
 * @param dirPath - absolute path like "/docs" or "/"
 * @param rootDirHash - hash of the root directory object
 * @param store - object store for fetching directory objects
 * @param deps - codec dependencies
 * @returns array of DirEntry, or null if directory not found
 */
export async function readdir(
    dirPath: string,
    rootDirHash: string,
    store: DirObjectStore,
    deps: DirTreeCodecDeps,
): Promise<DirEntry[] | null> {
    const parts = dirPath.split('/').filter(Boolean)

    let currentHash = rootDirHash
    const visited = new Set<string>()

    for (const name of parts) {
        if (visited.has(currentHash)) {
            throw new Error(`Cyclic directory reference detected at hash ${currentHash}`)
        }
        visited.add(currentHash)

        const ciphertext = await store.get(currentHash)
        if (!ciphertext) return null

        const dir = await decryptDirObject(ciphertext, deps)
        const entry = dir.entries.find(e => e.name === name)
        if (!entry || entry.kind !== 'directory') return null
        currentHash = entry.dirHash
    }

    // Fetch and decrypt the target directory
    const ciphertext = await store.get(currentHash)
    if (!ciphertext) return null
    const dir = await decryptDirObject(ciphertext, deps)
    return dir.entries
}

/**
 * Mutate a single entry in the tree, re-encrypting all affected directory objects
 * from the target up to the root.
 *
 * Returns the new rootDirHash and all new/changed directory objects.
 *
 * POSIX mtime bubbling: parent directory mtime is updated only for add/remove/rename,
 * NOT for content modification of existing files.
 */
export async function mutateEntry(
    path: string,
    rootDirHash: string | null,
    store: DirObjectStore,
    deps: DirTreeCodecDeps,
    mutation: { type: 'upsert'; entry: DirEntry } | { type: 'delete' },
): Promise<{ rootDirHash: string; objects: Map<string, Uint8Array> }> {
    const parts = path.split('/').filter(Boolean)
    if (parts.length === 0) throw new Error('Cannot mutate root directory directly')

    const objects = new Map<string, Uint8Array>()

    // Load path segments to build the chain from root to target
    const chain: Array<{ hash: string; dir: DirectoryObject }> = []
    let currentHash = rootDirHash

    for (let i = 0; i < parts.length - 1; i++) {
        if (!currentHash) {
            // Create missing intermediate directories
            const emptyDir = createDirObject([])
            const { ciphertext, hash } = await encryptDirObject(emptyDir, deps)
            objects.set(hash, ciphertext)
            chain.push({ hash, dir: emptyDir })
            currentHash = null  // next level also needs creation
            continue
        }

        const ciphertext = await store.get(currentHash)
        if (!ciphertext) throw new Error(`Directory object not found: ${currentHash}`)
        const dir = await decryptDirObject(ciphertext, deps)
        chain.push({ hash: currentHash, dir })

        const entry = dir.entries.find(e => e.name === parts[i])
        if (entry && entry.kind === 'directory') {
            currentHash = entry.dirHash
        } else if (entry) {
            throw new Error(`ENOTDIR: Not a directory: /${parts.slice(0, i + 1).join('/')}`)
        } else {
            currentHash = null  // will create intermediate dir
        }
    }

    // Load or create the target's parent directory
    let targetDir: DirectoryObject
    let targetHash: string
    if (currentHash) {
        const ciphertext = await store.get(currentHash)
        if (!ciphertext) throw new Error(`Directory object not found: ${currentHash}`)
        targetDir = await decryptDirObject(ciphertext, deps)
        targetHash = currentHash
    } else {
        targetDir = createDirObject([])
        const { ciphertext, hash } = await encryptDirObject(targetDir, deps)
        objects.set(hash, ciphertext)
        targetDir = targetDir
        targetHash = hash
    }

    // Apply mutation to the target directory
    const targetName = parts[parts.length - 1]
    const existingIdx = targetDir.entries.findIndex(e => e.name === targetName)
    const newEntries = [...targetDir.entries]
    const isStructuralChange = mutation.type === 'delete' || existingIdx === -1  // add or remove

    if (mutation.type === 'upsert') {
        if (existingIdx >= 0) {
            newEntries[existingIdx] = mutation.entry
        } else {
            newEntries.push(mutation.entry)
        }
    } else {
        if (existingIdx >= 0) {
            newEntries.splice(existingIdx, 1)
        }
    }

    // Encrypt the modified target directory
    const newTargetDir = createDirObject(newEntries)
    const { ciphertext: newTargetCiphertext, hash: newTargetHash } = await encryptDirObject(newTargetDir, deps)
    objects.set(newTargetHash, newTargetCiphertext)

    // Bubble up: update parent entries from bottom to top
    let childHash = newTargetHash
    // Derive dir mtime from child entries for determinism (not wall-clock)
    const childMtime = newTargetDir.entries.reduce(
        (mx, e) => Math.max(mx, 'mtime' in e ? (e as any).mtime ?? 0 : 0), 0,
    )

    for (let i = chain.length - 1; i >= 0; i--) {
        const parentDir = chain[i].dir
        const childName = parts[i]
        const parentEntries = [...parentDir.entries]
        const childIdx = parentEntries.findIndex(e => e.name === childName)

        if (childIdx >= 0) {
            // Update existing directory entry — mtime only on structural changes
            const existing = parentEntries[childIdx] as DirEntryDir
            parentEntries[childIdx] = {
                ...existing,
                dirHash: childHash,
                ...(isStructuralChange ? { mtime: childMtime } : {}),
            }
        } else {
            // Add new directory entry
            parentEntries.push({
                name: childName,
                kind: 'directory',
                mtime: childMtime,
                ctime: childMtime,
                dirHash: childHash,
            } as DirEntryDir)
        }

        const newParentDir = createDirObject(parentEntries)
        const { ciphertext, hash } = await encryptDirObject(newParentDir, deps)
        objects.set(hash, ciphertext)
        childHash = hash
    }

    // If no chain (file in root), childHash is the new root
    if (chain.length === 0) {
        return { rootDirHash: newTargetHash, objects }
    }

    return { rootDirHash: childHash, objects }
}

// ── State ↔ Flat file conversion ─────────────────────────────────

export interface FlatFileEntry {
    path: string
    kind: 'file' | 'directory' | 'symlink'
    mime: string
    size: number
    mtime: number
    ctime: number
    /** Existing Merkle child directory hash for explicit directory entries. */
    dirHash?: string
    lastWriterKid?: string
    target?: string            // symlink target (only when kind === 'symlink')
    content: ContentRef
    meta?: Record<string, unknown>
}

/**
 * Convert a Merkle tree (rootDirHash) into a flat list of files.
 *
 * This is the bridge between:
 *   v3 state (rootDirHash → Merkle tree) ↔ in-memory Map<string, MutableFileEntry>
 *
 * Recursively walks the tree, producing full paths.
 * Returns [] for null rootDirHash (empty workspace).
 */
export async function flattenStateToFiles(
    rootDirHash: string | null,
    store: DirObjectStore,
    deps: DirTreeCodecDeps,
): Promise<FlatFileEntry[]> {
    if (!rootDirHash) return []

    const result: FlatFileEntry[] = []
    await flattenDir('/', rootDirHash, store, deps, result, 0)
    return result
}

async function flattenDir(
    basePath: string,
    dirHash: string,
    store: DirObjectStore,
    deps: DirTreeCodecDeps,
    result: FlatFileEntry[],
    depth: number,
): Promise<void> {
    if (depth > MAX_TREE_DEPTH) throw new Error('Tree flatten exceeded maximum depth')

    const ciphertext = await store.get(dirHash)
    if (!ciphertext) return

    const dir = await decryptDirObject(ciphertext, deps)

    for (const entry of dir.entries) {
        const entryPath = basePath === '/' ? `/${entry.name}` : `${basePath}/${entry.name}`

        if (entry.kind === 'file') {
            const file = entry as DirEntryFile
            result.push({
                path: entryPath,
                kind: 'file',
                mime: file.mime,
                size: file.size,
                mtime: file.mtime,
                ctime: file.ctime,
                lastWriterKid: file.lastWriterKid,
                content: file.content,
                meta: file.meta,
            })
        } else if (entry.kind === 'directory') {
            const dirEntry = entry as DirEntryDir
            // Emit explicit directory entry so empty dirs survive round-trip
            result.push({
                path: entryPath + '/',
                kind: 'directory',
                mime: '',
                size: 0,
                mtime: dirEntry.mtime,
                ctime: dirEntry.ctime,
                dirHash: dirEntry.dirHash,
                content: { type: 'inline', hash: '' },
                meta: dirEntry.meta,
            })
            await flattenDir(entryPath, dirEntry.dirHash, store, deps, result, depth + 1)
        } else if (entry.kind === 'symlink') {
            const sym = entry as DirEntrySymlink
            result.push({
                path: entryPath,
                kind: 'symlink',
                mime: '',
                size: 0,
                mtime: sym.mtime,
                ctime: sym.ctime,
                lastWriterKid: sym.lastWriterKid,
                target: sym.target,
                meta: sym.meta,
                content: { type: 'inline', hash: '' },  // symlinks have no content
            })
        }
    }
}

// ── Merkle diff: O(changed-subtrees) instead of O(total-files) ──

export interface MerkleDiffEntry {
    path: string
    changeType: 'added' | 'deleted' | 'modified'
}

/**
 * Diff two Merkle directory trees without flattening them.
 * Only descends into subtrees whose root hashes differ.
 * For added/deleted directories, all descendants are included so that
 * `listForFile` history filtering continues to work correctly.
 */
export async function diffMerkleTrees(
    oldRootHash: string | null,
    newRootHash: string | null,
    store: DirObjectStore,
    deps: DirTreeCodecDeps,
): Promise<MerkleDiffEntry[]> {
    const result: MerkleDiffEntry[] = []
    await diffDir('/', oldRootHash, newRootHash, store, deps, result, 0)
    return result
}

async function diffDir(
    basePath: string,
    oldHash: string | null,
    newHash: string | null,
    store: DirObjectStore,
    deps: DirTreeCodecDeps,
    result: MerkleDiffEntry[],
    depth: number,
): Promise<void> {
    if (depth > MAX_TREE_DEPTH) throw new Error('Tree diff exceeded maximum depth')
    if (oldHash === newHash) return

    const oldDir = oldHash ? await loadDirForDiff(oldHash, store, deps) : null
    const newDir = newHash ? await loadDirForDiff(newHash, store, deps) : null

    if (!oldDir) {
        if (newDir && newHash) await addAllDescendants(basePath, newHash, store, deps, result, depth, 'added')
        return
    }
    if (!newDir) {
        if (oldHash) await addAllDescendants(basePath, oldHash, store, deps, result, depth, 'deleted')
        return
    }

    const oldMap = new Map(oldDir.entries.map(e => [e.name, e]))
    const newMap = new Map(newDir.entries.map(e => [e.name, e]))

    for (const [name, oldEntry] of oldMap) {
        const newEntry = newMap.get(name)
        const path = basePath === '/' ? `/${name}` : `${basePath}/${name}`
        if (!newEntry) {
            if (oldEntry.kind === 'directory') {
                await addAllDescendants(path, (oldEntry as DirEntryDir).dirHash, store, deps, result, depth + 1, 'deleted')
            } else {
                result.push({ path, changeType: 'deleted' })
            }
        } else if (entryChanged(oldEntry, newEntry)) {
            if (oldEntry.kind === 'directory' && newEntry.kind === 'directory') {
                await diffDir(
                    path,
                    (oldEntry as DirEntryDir).dirHash,
                    (newEntry as DirEntryDir).dirHash,
                    store,
                    deps,
                    result,
                    depth + 1,
                )
            } else {
                result.push({ path, changeType: 'modified' })
            }
        }
    }

    for (const [name, newEntry] of newMap) {
        if (!oldMap.has(name)) {
            const path = basePath === '/' ? `/${name}` : `${basePath}/${name}`
            if (newEntry.kind === 'directory') {
                await addAllDescendants(path, (newEntry as DirEntryDir).dirHash, store, deps, result, depth + 1, 'added')
            } else {
                result.push({ path, changeType: 'added' })
            }
        }
    }
}

function entryChanged(a: DirEntry, b: DirEntry): boolean {
    if (a.kind !== b.kind) return true
    switch (a.kind) {
        case 'file': {
            const af = a as DirEntryFile
            const bf = b as DirEntryFile
            return contentHash(af.content) !== contentHash(bf.content)
        }
        case 'directory':
            return (a as DirEntryDir).dirHash !== (b as DirEntryDir).dirHash
        case 'symlink':
            return (a as DirEntrySymlink).target !== (b as DirEntrySymlink).target
        default:
            return true
    }
}

function contentHash(ref: { type: 'inline'; hash: string } | { type: 'chunked'; manifestHash: string }): string {
    return ref.type === 'inline' ? ref.hash : ref.manifestHash
}

async function addAllDescendants(
    basePath: string,
    dirHash: string,
    store: DirObjectStore,
    deps: DirTreeCodecDeps,
    result: MerkleDiffEntry[],
    depth: number,
    changeType: 'added' | 'deleted',
): Promise<void> {
    if (depth > MAX_TREE_DEPTH) throw new Error('Tree diff exceeded maximum depth')
    const dir = await loadDirForDiff(dirHash, store, deps)
    if (!dir) return
    for (const entry of dir.entries) {
        const path = basePath === '/' ? `/${entry.name}` : `${basePath}/${entry.name}`
        result.push({ path, changeType })
        if (entry.kind === 'directory') {
            await addAllDescendants(path, (entry as DirEntryDir).dirHash, store, deps, result, depth + 1, changeType)
        }
    }
}

async function loadDirForDiff(
    hash: string,
    store: DirObjectStore,
    deps: DirTreeCodecDeps,
): Promise<DirectoryObject | null> {
    const ciphertext = await store.get(hash)
    if (!ciphertext) return null
    return decryptDirObject(ciphertext, deps)
}

// ── GC: Collect all referenced hashes ────────────────────────────

const SHA256_HEX_RE = /^[a-f0-9]{64}$/i

function addValidTreeHash(hashes: Set<string>, hash: string | null | undefined): void {
    if (typeof hash === 'string' && SHA256_HEX_RE.test(hash)) {
        hashes.add(hash.toLowerCase())
    }
}

/**
 * Walk the Merkle tree and collect ALL CAS hashes referenced by it.
 *
 * Returns a Set containing:
 *   - Directory object hashes (the dir objects themselves)
 *   - File content hashes (inline.hash, chunked.manifestHash)
 *
 * Used by GC to determine which objects are reachable and must not be evicted.
 * The visited set provides both dedup and cyclic graph protection.
 */
export async function collectTreeHashes(
    rootDirHash: string | null,
    store: DirObjectStore,
    deps: DirTreeCodecDeps,
): Promise<Set<string>> {
    const hashes = new Set<string>()
    if (!rootDirHash) return hashes

    await walkTree(rootDirHash, store, deps, hashes, 0)
    return hashes
}

async function walkTree(
    dirHash: string,
    store: DirObjectStore,
    deps: DirTreeCodecDeps,
    hashes: Set<string>,
    depth: number,
): Promise<void> {
    if (depth > MAX_TREE_DEPTH) throw new Error('GC tree walk exceeded maximum depth')
    if (hashes.has(dirHash)) return // already visited (dedup + cyclic protection)

    addValidTreeHash(hashes, dirHash)

    const ciphertext = await store.get(dirHash)
    if (!ciphertext) return

    const dir = await decryptDirObject(ciphertext, deps)

    for (const entry of dir.entries) {
        if (entry.kind === 'file') {
            const file = entry as DirEntryFile
            if (file.content.type === 'inline') {
                addValidTreeHash(hashes, file.content.hash)
            } else {
                addValidTreeHash(hashes, file.content.manifestHash)
            }
        } else if (entry.kind === 'directory') {
            await walkTree((entry as DirEntryDir).dirHash, store, deps, hashes, depth + 1)
        }
        // symlinks: no CAS objects to protect
    }
}
